import { AccountFormData } from 'types/account';
import intl from 'components/i18n/ReactIntlWrapper';

/**
 * Validates account number format
 * Rules: Alphanumeric, hyphens allowed, 4-20 characters
 */
export const validateAccountNumber = (accountNumber: string): string | true => {
  if (!accountNumber || accountNumber.trim() === '') {
    return intl.get('accountManagement.accountNumberRequired');
  }
  
  if (accountNumber.length < 4) {
    return intl.get('accountManagement.accountNumberMinLength');
  }
  
  if (accountNumber.length > 20) {
    return intl.get('accountManagement.accountNumberMaxLength');
  }
  
  const validFormat = /^[A-Za-z0-9-]+$/;
  if (!validFormat.test(accountNumber)) {
    return intl.get('accountManagement.accountNumberInvalidFormat');
  }
  
  return true;
};

/**
 * Validates account name
 * Rules: 3-100 characters, no special characters except spaces and hyphens
 */
export const validateAccountName = (accountName: string): string | true => {
  if (!accountName || accountName.trim() === '') {
    return intl.get('accountManagement.accountNameRequired');
  }
  
  if (accountName.trim().length < 3) {
    return intl.get('accountManagement.accountNameMinLength');
  }
  
  if (accountName.length > 20) {
    return intl.get('accountManagement.accountNameMaxLength');
  }
  
  const validFormat = /^[A-Za-z0-9\s-]+$/;
  if (!validFormat.test(accountName)) {
    return intl.get('accountManagement.accountNameInvalidFormat');
  }
  
  return true;
};

/**
 * Validates account description
 * Rules: Optional, max 200 characters
 */
export const validateDescription = (description?: string): string | true => {
  if (!description) return true;
  
  if (description.length > 200) {
    return intl.get('accountManagement.descriptionMaxLength');
  }
  
  return true;
};

/**
 * Validates complete account form data
 */
export const validateAccountForm = (data: AccountFormData): Record<string, string> => {
  const errors: Record<string, string> = {};

  const accountNumberError = validateAccountNumber(data.accountNumber);
  if (accountNumberError !== true) {
    errors.accountNumber = accountNumberError;
  }

  const accountNameError = validateAccountName(data.accountName);
  if (accountNameError !== true) {
    errors.accountName = accountNameError;
  }

  // Currency validation removed - now handled by dropdown with required rule

  const descriptionError = validateDescription(data.description);
  if (descriptionError !== true) {
    errors.description = descriptionError;
  }

  if (!data.accountTypeId) {
    errors.accountTypeId = intl.get('accountManagement.accountTypeRequired');
  }

  return errors;
};

/**
 * Checks if account number is unique (async validation)
 * This should be called separately as it requires API call
 */
export const checkAccountNumberUniqueness = async (
  accountNumber: string,
  existingAccountId?: number
): Promise<boolean> => {
  // TODO: Implement API call to check uniqueness
  // For now, return true (assuming unique)
  return true;
};
